import { h } from "preact";
import { useState, useEffect, useCallback } from "preact/hooks";
import { Typography, Avatar, Skeleton, Tooltip } from "antd";
import {
    getTransactionErrorDescription,
    getTransactionStatus,
    getTransactionStatusType,
    getTransactionTimeAtUTC,
    convertToCountryName,
    convertDateFormat,
    getFlagEmoji, getVendorDisplay, extractVendorsFromTransaction, extractVendorVersionsFromTransaction,
    extractFlowFormIdentity,
} from "./TransactionsUtils";
import useAxios from "../../hooks/useAxios";
import { UserOutlined } from "@ant-design/icons";
import useAxiosCallback from "../../hooks/useAxiosCallback.js";

export default function TransactionsDataSource(transactions) {
  const entries = transactions || [];
  const total = (transactions && transactions.length) || 0;
  const [imageUrls, setImageUrls] = useState({});
  const { fetch } = useAxiosCallback();
  const fetchImage = useCallback(
    async (flow_id) => {
      const fetchAndSetImage = async (label) => {
        try {
          const response = await fetch(`/admin/resources/file`, {
            params: {
              flow_id: flow_id,
              label: label,
              tag: "thumbnail",
            },
          });
          setImageUrls((prevImageUrls) => ({
            ...prevImageUrls,
            [flow_id]: response.data,
          }));
          return true;
        } catch (error) {
          if (error.response?.status === 404) {
            return false;
          }
          throw error;
        }
      };

      try {
        const found = await fetchAndSetImage("bestframe");
        if (found) return;
      } catch (error) {
        throw error;
      }

      try {
        const foundFront = await fetchAndSetImage("doc_original_front_img");
        if (foundFront) return;
      } catch (error) {
        throw error;
      }

      try {
        const foundOriginal = await fetchAndSetImage("doc_original_img");
        if (foundOriginal) return;
      } catch (error) {
        throw error;
      }

      try {
        await fetchAndSetImage("doc_original_back_img");
      } catch (error) {
        throw error;
      }
    },
    [setImageUrls]
  );

  useEffect(() => {
    for (const transaction of entries) {
      const flow_id = transaction?.flow_id;
      if (!imageUrls[flow_id]) {
        fetchImage(flow_id);
      }
    }
  }, [entries, fetchImage]);

  function asTableView(handleFlowIdClick) {
    return {
      columns: [
        {
          title: "Image",
          dataIndex: "image",
          render: (image) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {imageUrls[image.flow_id] ? (
                <img
                  src={imageUrls[image.flow_id]}
                  alt={image._id + " photo"}
                />
              ) : (
                <Skeleton.Node active={true} size={"default"}>
                  <UserOutlined style={{ fontSize: 64, color: "#ffffff" }} />
                </Skeleton.Node>
              )}
            </div>
          ),
        },
        {
          title: "Flow ID",
          dataIndex: "flow_id",
          render: (flow_id) => (
            <div style={{ maxWidth: "150px" }}>
              <Typography.Text
                copyable
                onClick={(e) => {
                  e.preventDefault();
                  handleFlowIdClick(flow_id);
                }}
                style={{
                  color: "var(--ant-primary-color)",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {flow_id}
              </Typography.Text>
            </div>
          ),
        },
        {
          title: "Name",
          dataIndex: "name",
          render: (name) => (
            <Typography.Text style={{ fontSize: "12px" }}>
              {name}
            </Typography.Text>
          ),
        },
        {
          title: "Person ID",
          dataIndex: "person",
          render: (person) => (
            <Typography.Text style={{ fontSize: "12px" }}>
              {person._id}
            </Typography.Text>
          ),
        },
        {
          title: "IIN",
          dataIndex: "iin",
          render: (iin) => (
          <Typography.Text style={{ fontSize: "12px" }}>
              {iin}
          </Typography.Text>
          )
        },
        {
          title: "Time",
          dataIndex: "created_at",
          sorter: (a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);

            if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
              console.error("Invalid date format detected.");
              return 0;
            }

            return dateA - dateB; // Ascending order
          },
          sortDirections: ["ascend", "descend", "ascend"],
          render: (created_at) => (
            <Typography.Text style={{ fontSize: "12px" }}>
              {created_at}
            </Typography.Text>
          ),
        },
        {
          title: "Country",
          dataIndex: "country",
          align: "center",
          render: (country) => (
            <div style={{ display: "flex", justifyContent: "center", cursor: "pointer" }}>
              <Tooltip title={convertToCountryName(country)}>
                <div style={{ fontSize: "24px" }}>
                  {getFlagEmoji(country)}
                </div>
              </Tooltip>
            </div>
          ),
        },
        {
            title: "Vendors",
            dataIndex: "vendor",
            align: "center",
            render: (vendor, record) => {
                if (!vendor || vendor.length === 0) {
                    return <span>-</span>;
                }

                const vendorCodes = Array.isArray(vendor) ? vendor : [vendor];
                const vendors = record.vendorVersions || vendorCodes.map(vendorCode => ({ vendorCode }));

                return(
                    <div style={{ display: "flex", alignItems: 'center', justifyContent: "center", gap: "8px" }}>
                        {vendors.map(({ vendorCode, livenessVersion }, index) => (
                            <span key={`${vendorCode}-${livenessVersion || "unknown"}-${index}`}>
                                {getVendorDisplay(vendorCode, { livenessVersion, showName: false, iconSize: 24, gap: "12px" })}
                            </span>
                        ))}
                    </div>
                )
            },
        },
        {
          title: "Status",
          dataIndex: "status",
          render: (status) => (
            <Typography.Text
              type={getTransactionStatusType(status)}
              style={{ fontSize: "12px" }}
            >
              {status === "pass"
                ? "approved"
                : status === "fail"
                ? "denied"
                : status}
            </Typography.Text>
          ),
        },
        {
          title: "Type of check",
          dataIndex: "type_of_check",
          render: (type_of_check) => (
            <Typography.Text style={{ fontSize: "12px" }}>
              {type_of_check}
            </Typography.Text>
          ),
        },
      ],
      rows:
        entries && Array.isArray(entries)
          ? entries.map((transaction, index) => ({
              key: index,
              // session: { _id: transaction.session_id, service_name: transaction.service_name },
              flow_id: transaction.flow_id,
              country: transaction.country_code,
              person: { _id: transaction.person_id },
              iin: transaction.iin ?? extractFlowFormIdentity(transaction)?.iin ?? "-",
              image: {
                flow_id: transaction.flow_id,
                _id: transaction.person_id,
              },
              name:
                transaction.person_name != "-"
                  ? transaction.person_name
                  : (() => {
                      let foundName = "-";
                      transaction.service_statuses?.forEach((status) => {
                        if (
                          ["egov-doc", "veridoc", "redoc"].includes(
                            status.service_name
                          )
                        ) {
                          if (status.session_result?.document?.full_name) {
                            foundName =
                              status.session_result.document.full_name;
                          }
                          if (
                            status.session_result?.document?.first_name &&
                            status.session_result?.document?.last_name
                          ) {
                            foundName =
                              status.session_result.document.first_name +
                              " " +
                              status.session_result.document.last_name;
                          }
                        }
                      });
                      return foundName;
                    })(),
              // service_name: transaction.service_name,
              created_at: convertDateFormat(transaction.created_at),
              // status: getTransactionStatus(transaction),
              status: transaction.flow_status,
              vendor: extractVendorsFromTransaction(transaction),
              vendorVersions: extractVendorVersionsFromTransaction(transaction),
              type_of_check:
                transaction.check_type === "auto" ? "auto" : "manual",
              // description: getTransactionErrorDescription(transaction)
            }))
          : [],
      total: total,
    };
  }

  return { asTableView };
}
